import { useEffect } from 'react';
import logger from '@finn-no/fiaas-logger';
import { trackOutPageView } from '@/lib/tracking/tracking';
import { Box } from '@fabric-ds/react';
import { Head } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { FinnLogo } from 'shared/icons/FinnLogo';
import { SegmentPixel } from 'shared/util/SegmentPixel';
import getConfig from 'next/config';

const redirectDelay = 3000;

const {
    serverRuntimeConfig: { baseUrl, apiBaseUrl },
} = getConfig();

export interface OutLinkData {
    deepLink: string | null;
    logo: string | null;
    brand: string | null;
    externalUrl: string | null;
}

interface FhhOutPageProps {
    outLinkData: OutLinkData | null;
    fullDeepLink: string;
}

const FhhOutPage = ({ outLinkData, fullDeepLink }: FhhOutPageProps) => {
    useEffect(() => {
        trackOutPageView();

        setTimeout(() => {
            if (!window.location.href.match(/\?.*doNotRedirect/)) {
                window.location.replace(fullDeepLink);
            }
        }, redirectDelay);
    }, []);

    return outLinkData ? (
        <>
            <Head
                title="Du forlater nå FINN reise"
                canonical={`${baseUrl}/reise/feriehus-hytteutleie/ut/`}
                description={`Du forlater nå FINN reise og kan gjennomføre bestillingen hos ${outLinkData.brand}.`}
            />

            <Main className="space-y-32">
                <h1 className="text-center text-28 mt-64">Du forlater nå FINN reise</h1>
                <Box info as="section" className="text-center space-y-32">
                    <div className="text-22" data-testid="mainText">
                        <span className="font-bold">Husk:</span> {outLinkData.brand} tar i mot bestillingen og må kontaktes dersom du har
                        spørsmål
                    </div>
                    <div data-testid="tipText">
                        <span className="font-bold">Tips:</span> Sammenlign priser for <span className="font-bold">hotell</span> og{' '}
                        <span className="font-bold">leiebil</span> på FINN reise!
                    </div>
                </Box>
                <Box as="section" className="grid grid-cols-1 sm:grid-cols-3 gap-y-32 place-items-center">
                    <FinnLogo />
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        shapeRendering="geometricPrecision"
                        textRendering="geometricPrecision"
                        viewBox="0 0 256 4"
                        style={{ whiteSpace: 'pre' }}>
                        <rect width="256" height="4" fill="#DFE4E8" stroke="none" transform="translate(128,2) translate(-128,-2)" />
                        <rect
                            width="10"
                            height="4"
                            fill="#0063FB"
                            stroke="none"
                            transform="translate(71,2) translate(-71,-2)"
                            className="outpage-progress"
                        />
                    </svg>
                    <img
                        src={`https://images.finncdn.no/dynamic/1600w/logo/${outLinkData.logo}`}
                        height="22px"
                        width="66px"
                        data-testid="supplierLogo"
                        alt={outLinkData.brand ?? undefined}
                        onError={() => 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'}
                    />
                </Box>
            </Main>
            {/* Segment Pixel - NO-SNO-RT: FINN Reise Klikk Ut Feriehjem - DO NOT MODIFY */}
            <SegmentPixel id={'30128950'} />
            {/* End of Segment Pixel */}
        </>
    ) : null;
};

export const getServerSideProps = async ({ query }) => {
    const adId = query?.finnkode;

    if (!adId) {
        logger.warn('adId is missing');

        return {
            notFound: true,
        };
    }

    const queryParams = query?.deepLinkParams;
    try {
        const response = await fetch(`${apiBaseUrl}/object/info/${adId}`);
        if (response.ok) {
            const outLinkData = await response.json();
            const decodedQueryParams = atob(queryParams);
            const fullDeepLink = generateFullDeepLink(outLinkData, decodedQueryParams);
            return {
                props: { outLinkData, fullDeepLink },
                notFound: outLinkData?.deepLink === null && outLinkData?.externalUrl === null,
            };
        } else {
            logger.warn(`No outLinkData available from the api for adId: ${adId}; status code: ${response.status}`);

            return {
                notFound: true,
            };
        }
    } catch (e) {
        logger.warn(e);

        return {
            notFound: true,
        };
    }
};

const generateFullDeepLink = (outLinkData: OutLinkData, decodedQueryParams): string => {
    if (outLinkData.brand === 'dancenter') {
        const url = outLinkData.deepLink ?? outLinkData.externalUrl ?? null;
        const urlWithParams = new URL(`${url}${decodedQueryParams}`);
        const params = urlWithParams.searchParams;

        if (decodedQueryParams !== '' && params.has('firstdate') && params.has('days')) {
            return `${outLinkData.deepLink}&firstdate=${params.get('firstdate')}&days=${params.get('days')}`;
        } else {
            return `${outLinkData.deepLink}`;
        }
    } else if (outLinkData.externalUrl !== null && outLinkData.externalUrl !== '') {
        return `${outLinkData.externalUrl}`;
    } else {
        return `${outLinkData.deepLink}`;
    }
};

export default FhhOutPage;
